// Shared types for User Groups — customizable distribution lists that let admins organize
// individuals into subsections. A group can be assigned wholesale as subscribers to
// announcements, work orders and walk-throughs, or as a primary facility contact; because the
// assignment references the group rather than a copy of its members, it resolves to whoever is
// in the group at the time it is used.

// The index page's target collection. A brand-new target collection must also be registered in
// `standardInitialStateGenerator` inside `src/reducers/user_groups_reducer.ts`, or
// `PaginatedReduxTable` throws at render time.
export const USER_GROUPS_INDEX_TARGET_COLLECTION = 'userGroupsIndex';

// Mirrors `models.usergroup.UserGroup` on the backend branch `palani/ope-5239` as of
// "Rework user groups onto the standard CRUD framework". NOT the 2023 OPE-823 branch, which
// is superseded and disagrees on the route, the membership field and the tenant column.
export interface UserGroup {
	id: number;
	name: string;
	// Member contact emails — the whole membership record, and the ONLY writable field besides
	// `name`. The controller reads it with `optValueSeq("memberEmails")`, so a wrong or missing
	// key is not an error: it resolves to an empty Seq, and on update the service then removes
	// every existing member. That is why sending `usersList`/`memberList` silently emptied
	// groups instead of failing loudly.
	memberEmails: string[];
	// Hydrated by the service layer on BOTH the list and the single-GET, derived from each
	// member's account status. Note the split is active vs **invited** (ContactStatus.Active /
	// ContactStatus.Invited) — there is no "inactive" count. Members whose contact no longer
	// resolves in the facility are dropped from both the counts and `memberEmails`.
	activeUsersCount?: number;
	invitedUsersCount?: number;
	// Server-owned tenant, forced from the authenticated contact — a client-supplied value is
	// ignored. Exactly one is set: buyer groups are COMPANY-wide (so a member may belong to any
	// facility of the company), while supplier groups are scoped to the supplier facility.
	buyerCompanyId?: number;
	supplierFacilityId?: number;
	createdBy?: string;
	updatedBy?: string;
	createdAt?: string;
	updatedAt?: string;
	isDeleted?: boolean;
	deletedAt?: string;
}

// Backs the multi-select in the invite / edit user modals — every group in the tenant, so the
// picker can offer them. Separate from the index page's collection so the two never fight over
// `getTakeLatest`, which cancels an in-flight read whenever a newer one shares its target
// collection name (`null` counts as a name).
export const USER_GROUPS_PICKER_TARGET_COLLECTION = 'userGroupsPicker';

// The groups one user already belongs to, fetched with the `memberEmail` filter to seed that
// same picker when an existing user is opened. Distinct collection for the same reason: it is
// issued in parallel with the picker fetch above.
export const USER_GROUPS_FOR_MEMBER_TARGET_COLLECTION = 'userGroupsForMember';

// The list endpoint defaults to a limit of 10 and logs a warning above 50, so 50 is the most a
// single read can ask for. Tenants with more groups than this reach the rest through the
// picker's server-side `search`.
export const USER_GROUPS_PAGE_SIZE = 50;
